// =============================================================================
// VandieZ admin-web -- Ops trend chart (inline SVG sparkline)
// =============================================================================
// Tiny dependency-free chart on the Ops dashboard. Renders a single
// metric (GMV by default; switchable via the prop) as a smoothed area
// chart spanning the last N days of the OpsDailyResponse.
//
// Why inline SVG instead of recharts: every existing chart need on
// admin-web is a single line / area, and recharts pulls in ~120 KB
// gzipped. Keeping it inline is ~50 lines + no bundle bloat. When the
// dashboard grows to multi-axis + tooltips we'll switch to recharts.
// =============================================================================

import type React from "react";
import { useMemo } from "react";

import { cn } from "@/lib/utils";
import type { DailyKpiRow } from "@/types/ops";

type Metric =
  | "gmv"
  | "commission"
  | "completed_trips"
  | "new_signups"
  | "new_disputes";

const METRIC_LABEL: Record<Metric, string> = {
  gmv: "GMV",
  commission: "Commission",
  completed_trips: "Completed trips",
  new_signups: "New signups",
  new_disputes: "Disputes",
};

const METRIC_TONE: Record<Metric, string> = {
  gmv: "stroke-emerald-500 fill-emerald-500/15",
  commission: "stroke-blue-500 fill-blue-500/15",
  completed_trips: "stroke-violet-500 fill-violet-500/15",
  new_signups: "stroke-sky-500 fill-sky-500/15",
  new_disputes: "stroke-rose-500 fill-rose-500/15",
};

function valueOf(row: DailyKpiRow, metric: Metric): number {
  switch (metric) {
    case "gmv":
      return Number.parseFloat(row.gmv);
    case "commission":
      return Number.parseFloat(row.commission);
    case "completed_trips":
      return row.completed_trips;
    case "new_signups":
      return row.new_signups;
    case "new_disputes":
      return row.new_disputes;
  }
}

function formatINR(n: number): string {
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

interface Props {
  rows: DailyKpiRow[];
  metric: Metric;
  height?: number;
}

export function OpsTrendChart({
  rows,
  metric,
  height = 120,
}: Props): React.JSX.Element {
  const { path, area, total, maxV, lastV } = useMemo(() => {
    if (rows.length === 0) {
      return { path: "", area: "", total: 0, maxV: 0, lastV: 0 };
    }
    const values = rows.map((r) => valueOf(r, metric));
    const maxLocal = Math.max(1, ...values);
    const w = 100; // viewBox width (percent)
    const h = 100; // viewBox height (percent)
    const stepX = rows.length > 1 ? w / (rows.length - 1) : w;
    const points = values.map((v, i) => {
      const x = i * stepX;
      const y = h - (v / maxLocal) * h;
      return { x, y };
    });
    const lineStr = points
      .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
      .join(" ");
    const areaStr = points.length > 0
      ? `${lineStr} L${(points[points.length - 1] ?? { x: 0 }).x.toFixed(2)},${h} L0,${h} Z`
      : "";
    return {
      path: lineStr,
      area: areaStr,
      total: values.reduce((a, b) => a + b, 0),
      maxV: maxLocal,
      lastV: values[values.length - 1] ?? 0,
    };
  }, [rows, metric]);

  const isMoney = metric === "gmv" || metric === "commission";
  const formatBig = (n: number): string =>
    isMoney ? formatINR(n) : n.toLocaleString("en-IN");

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            {METRIC_LABEL[metric]} (last {rows.length} days)
          </p>
          <p className="text-2xl font-semibold tabular-nums">
            {formatBig(total)}
          </p>
        </div>
        <p className="text-xs text-muted-foreground tabular-nums">
          peak {formatBig(maxV)} · today {formatBig(lastV)}
        </p>
      </div>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className={cn("w-full", METRIC_TONE[metric])}
        style={{ height }}
        role="img"
        aria-label={`${METRIC_LABEL[metric]} trend`}
      >
        <path d={area} strokeWidth="0" />
        <path d={path} fill="none" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}
